/**
 * What a tool returned, drawn.
 *
 * The export puts ride cards inside Smolit's bubbles, not a description of
 * rides — and the difference is not decoration: a card can be tapped through to
 * the ride, and a sentence cannot. Every attachment the tool layer emits has a
 * component here; anything without one renders nothing rather than dumping
 * JSON at the member.
 *
 * The ride card is the export's own, reproduced from the `m.rides` block of the
 * Smolit dock: 32px avatar, 13px bold `{departure} · {name}`, 11.5px
 * `★ {rating} · {duration}`, and the price at 15px/800 in pōhutukawa.
 */

import { useNavigate } from 'react-router';
import { Avatar, Button, cn } from '@openroad/ui';
import type { Attachment } from '../../lib/useSmolit';

interface Money {
  cents: number;
  currency: string;
}

interface RideCardData {
  id: string;
  departure: string;
  day: string;
  origin: string;
  destination: string;
  durationSeconds: number;
  price: Money;
  seatsAvailable: number;
  instantBook: boolean;
  co2SavedGrams: number;
  driver: {
    id: string;
    name: string;
    initials: string;
    avatarColour: string;
    rating: number | null;
    reviewCount: number;
  };
}

function money(value: Money | null | undefined): string {
  if (!value) return '—';
  const amount = (value.cents / 100).toFixed(2).replace(/\.00$/, '');
  return value.currency === 'NZD' ? `$${amount}` : `${amount} ${value.currency}`;
}

/** Rounded to whole minutes before the split, or 1h 59m 30s reads "1h 60". */
function duration(seconds: number): string {
  const total = Math.round(seconds / 60);
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  return `${hours}h ${String(minutes).padStart(2, '0')}`;
}

function co2(grams: number): string {
  return grams >= 1000 ? `${(grams / 1000).toFixed(1).replace(/\.0$/, '')} kg` : `${grams} g`;
}

function RideRow({ ride }: { ride: RideCardData }) {
  const navigate = useNavigate();

  return (
    <button
      type="button"
      onClick={() => navigate(`/trips/${ride.id}`)}
      className={cn(
        'flex w-full cursor-pointer items-center gap-2.5 rounded-lg border border-line',
        'bg-surface px-3 py-2.5 text-left transition-[transform,box-shadow] duration-fast',
        'ease-standard hover:-translate-y-px hover:shadow-ride',
      )}
    >
      <Avatar
        initials={ride.driver.initials}
        colour={ride.driver.avatarColour}
        size={32}
        name={ride.driver.name}
      />
      <span className="flex-1">
        <span className="block text-label font-bold">
          {ride.departure} · {ride.driver.name}
        </span>
        <span className="block text-mini text-muted">
          {ride.driver.rating ? `★ ${ride.driver.rating}` : 'New driver'} ·{' '}
          {duration(ride.durationSeconds)}
        </span>
      </span>
      <span className="font-display text-base font-extrabold text-pohutukawa">
        {money(ride.price)}
      </span>
    </button>
  );
}

function Rides({ data }: { data: { rides: RideCardData[]; total: number; date: string } }) {
  return (
    <>
      {data.rides.map((ride) => (
        <RideRow key={ride.id} ride={ride} />
      ))}
      {data.total > data.rides.length ? (
        <span className="text-mini text-subtle">
          {data.total - data.rides.length} more on {data.date}
        </span>
      ) : null}
    </>
  );
}

function BookingResult({
  data,
}: {
  data: {
    id: string;
    status: string;
    paymentStatus: string;
    seats: number;
    total: Money;
    co2SavedGrams: number;
    pickupPoint: string | null;
    trip: RideCardData;
  };
}) {
  const navigate = useNavigate();
  const confirmed = data.status === 'CONFIRMED';

  return (
    <div className="rounded-lg border border-line bg-surface p-3">
      <div className="mb-1.5 flex items-baseline justify-between gap-2">
        <span className="text-label font-bold">
          {confirmed ? 'Seat confirmed' : 'Request sent'}
        </span>
        <span className="font-display text-base font-extrabold text-pohutukawa">
          {money(data.total)}
        </span>
      </div>
      <p className="m-0 text-mini text-muted">
        {data.trip.origin} → {data.trip.destination}, {data.trip.day}, with {data.trip.driver.name}
        {data.pickupPoint ? ` · ${data.pickupPoint}` : ''}
      </p>
      <p className="m-0 mt-1 text-mini text-ngahere">Saves {co2(data.co2SavedGrams)} of CO₂</p>
      <Button
        variant="secondary"
        size="sm"
        className="mt-2.5 w-full"
        onClick={() => navigate(`/bookings/${data.id}/confirmed`)}
      >
        See the booking
      </Button>
    </div>
  );
}

function Impact({
  data,
}: {
  data: {
    kilogramsSaved: number;
    tripsShared: number;
    treesEquivalent: number;
    rank: number | null;
    region: string | null;
  };
}) {
  return (
    <div className="rounded-lg border border-ngahere-line bg-ngahere-tint p-3.5 text-center">
      <div className="font-display text-display-xs font-extrabold text-ngahere">
        {data.kilogramsSaved} kg
      </div>
      <div className="text-mini text-ngahere-ink">
        CO₂ saved across {data.tripsShared} shared {data.tripsShared === 1 ? 'ride' : 'rides'} ·{' '}
        {data.treesEquivalent} {data.treesEquivalent === 1 ? 'tree' : 'trees'} for a year
      </div>
      {data.rank ? (
        <div className="mt-1.5 inline-flex rounded-full bg-surface px-2.5 py-1 text-mini font-semibold text-ngahere">
          Rank #{data.rank}
          {data.region ? ` · ${data.region} region` : ''}
        </div>
      ) : null}
    </div>
  );
}

function PriceBand({
  data,
}: {
  data: {
    distanceMetres: number;
    suggested: Money;
    low: Money;
    high: Money;
    driverEarnings: Money;
  };
}) {
  return (
    <div className="rounded-lg border border-line bg-surface p-3">
      <div className="flex items-baseline justify-between">
        <span className="text-mini text-muted">
          {money(data.low)}–{money(data.high)} is fair
        </span>
        <span className="font-display text-title-sm font-extrabold text-pohutukawa">
          {money(data.suggested)}
        </span>
      </div>
      <p className="m-0 mt-1 text-mini text-muted">
        Over {Math.round(data.distanceMetres / 1000)} km. You would receive{' '}
        {money(data.driverEarnings)} after the service fee.
      </p>
    </div>
  );
}

/**
 * A drafted description, in a box the member can copy out of.
 *
 * The export has no such block — there is no publish flow behind Smolit in the
 * prototype. It is assembled from the design's own card, border and type, and
 * exists because a draft the member has to retype from a paragraph is not a
 * draft. Recorded in docs/design-fidelity.md.
 */
function Draft({ data }: { data: { description: string } }) {
  return (
    <div className="rounded-lg border border-line bg-sunk p-3">
      <div className="mb-1.5 text-caption font-bold tracking-eyebrow text-subtle uppercase">
        Suggested description
      </div>
      <p className="m-0 text-body-sm leading-normal whitespace-pre-wrap text-ink">
        {data.description}
      </p>
    </div>
  );
}

function Plan({
  data,
}: {
  data: {
    via: string;
    options: Array<{
      first: RideCardData;
      second: RideCardData;
      waitMinutes: number;
      totalPrice: Money;
    }>;
  };
}) {
  return (
    <>
      {data.options.map((option, index) => (
        <div key={index} className="rounded-lg border border-line bg-surface p-3">
          <div className="mb-2 flex items-baseline justify-between">
            <span className="text-label font-bold">Via {data.via}</span>
            <span className="font-display text-base font-extrabold text-pohutukawa">
              {money(option.totalPrice)}
            </span>
          </div>
          <div className="flex flex-col gap-2">
            <RideRow ride={option.first} />
            <span className="text-mini text-subtle">
              {option.waitMinutes} min to change in {data.via}
            </span>
            <RideRow ride={option.second} />
          </div>
        </div>
      ))}
    </>
  );
}

function SentMessage({ data }: { data: { to: string | null; body: string } }) {
  return (
    <div className="rounded-lg border border-line bg-surface p-3">
      <div className="mb-1 text-mini text-subtle">Sent{data.to ? ` to ${data.to}` : ''}</div>
      <p className="m-0 text-body-sm leading-normal text-ink">{data.body}</p>
    </div>
  );
}

function Checkout({ data }: { data: { url: string; amount: Money } }) {
  return (
    <a
      href={data.url}
      target="_blank"
      rel="noreferrer noopener"
      className="block rounded-lg border border-line bg-surface p-3 no-underline hover:shadow-ride"
    >
      <div className="text-label font-bold text-ink">Pay {money(data.amount)} on Stripe</div>
      <div className="text-mini text-muted">Opens Stripe’s own checkout in a new tab.</div>
    </a>
  );
}

function Profile({ data }: { data: { displayName: string; bio: string | null } }) {
  return (
    <div className="rounded-lg border border-line bg-surface p-3">
      <div className="text-label font-bold">{data.displayName}</div>
      <p className="m-0 mt-0.5 text-mini text-muted">{data.bio ?? 'No bio yet.'}</p>
    </div>
  );
}

/**
 * One attachment, drawn as whatever it is.
 *
 * The cast is unavoidable — the payload crosses the wire as JSON — and it is
 * contained here rather than spread through the panel. An unknown kind renders
 * nothing: a future tool that adds an attachment this build does not know how
 * to draw should be invisible, not broken.
 */
export function SmolitResult({ attachment }: { attachment: Attachment }) {
  const data = attachment.data as never;

  switch (attachment.kind) {
    case 'rides':
      return <Rides data={data} />;
    case 'ride':
      return <RideRow ride={data} />;
    case 'booking':
      return <BookingResult data={data} />;
    case 'impact':
      return <Impact data={data} />;
    case 'price':
      return <PriceBand data={data} />;
    case 'draft':
      return <Draft data={data} />;
    case 'plan':
      return <Plan data={data} />;
    case 'message':
      return <SentMessage data={data} />;
    case 'checkout':
      return <Checkout data={data} />;
    case 'profile':
      return <Profile data={data} />;
    default:
      return null;
  }
}
